import { useState, useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import useSiteSettings from '@/hooks/useSiteSettings';
import { Plus, Minus } from 'lucide-react';

const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

function FAQItem({ q, a, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;
  return (
    <div className="border-b border-[#A0743A]/15" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpenIndex(isOpen ? -1 : index)}
        className="w-full flex items-start justify-between gap-6 py-6 text-left hover:bg-[#A0743A]/5 transition-colors px-2"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="text-lg md:text-xl text-[#FAF8F5] leading-snug" style={{ fontFamily: FONT_HEADING }}>
          {q}
        </span>
        <span className="flex-shrink-0 mt-1 text-[#A0743A]">
          {isOpen ? <Minus size={20} strokeWidth={1.5} /> : <Plus size={20} strokeWidth={1.5} />}
        </span>
      </button>
      {isOpen && (
        <div className="pb-6 px-2 animate-fade-in-up">
          <p className="text-[#999] text-base leading-relaxed" style={{ fontFamily: FONT_BODY }}>
            {a}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const s = useSiteSettings() || {};
  const [openIndex, setOpenIndex] = useState(0);

  // Set page title
  useEffect(() => {
    const brand = s.brand_name || 'Sàwai Capital';
    const headline = s.faq_section_headline || 'Frequently Asked Questions';
    document.title = `${headline} — ${brand}`;
  }, [s.brand_name, s.faq_section_headline]);

  const faqs = [];
  for (let i = 1; i <= 10; i++) {
    const q = s[`faq_${i}_q`];
    const a = s[`faq_${i}_a`];
    if (q && q.trim() && a && a.trim()) {
      faqs.push({ q, a });
    }
  }

  return (
    <div className="bg-[#1A1A1A] min-h-screen" data-testid="faq-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-36 pb-16 px-6 md:px-12 border-b border-[#A0743A]/15">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>
            {s.faq_section_eyebrow || 'Frequently Asked Questions'}
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-[#FAF8F5] mb-6 tracking-tight leading-[1.1]" style={{ fontFamily: FONT_HEADING }}>
            {s.faq_section_headline || 'Answers for Serious Investors'}
          </h1>
          <div className="w-16 h-[1px] bg-[#C9A96E] mx-auto mb-6" />
          <p className="text-[#999] text-base md:text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: FONT_BODY }}>
            {s.faq_section_subtitle || 'Everything you need to know about accessing private market opportunities through the Dealroom.'}
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {faqs.length === 0 ? (
            <p className="text-[#666] text-center text-sm italic" style={{ fontFamily: FONT_BODY }}>
              FAQs coming soon.
            </p>
          ) : (
            <div className="border-t border-[#A0743A]/15">
              {faqs.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} index={i} openIndex={openIndex} setOpenIndex={setOpenIndex} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="py-16 px-6 md:px-12 bg-[#FAF8F5]/5 border-y border-[#A0743A]/15">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl text-[#FAF8F5] mb-4 tracking-tight" style={{ fontFamily: FONT_HEADING }}>
            Still have questions?
          </h2>
          <p className="text-[#999] mb-8 text-base" style={{ fontFamily: FONT_BODY }}>
            Our team is here to help. Reach out directly and we will get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${s.contact_email || 'dealroom@sawaicap.com'}`}
              className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A96E] transition-colors"
              style={{ fontFamily: FONT_BODY }}
              data-testid="faq-contact-email-btn"
            >
              Email Us
            </a>
            {s.whatsapp_number && (
              <a
                href={`https://wa.me/${s.whatsapp_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-[#A0743A] text-[#A0743A] px-8 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#A0743A]/10 transition-colors"
                style={{ fontFamily: FONT_BODY }}
                data-testid="faq-whatsapp-btn"
              >
                WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer s={s} />
    </div>
  );
}
